/**
 * <s-site-update> keeps the installed web app (and long-open tabs) on the latest storefront automatically.
 * When the app comes back into view, comes back online, or every 10 minutes while visible, it fetches a
 * tiny version stamp (/?view=site-version, falling back to the home page's <meta name="site-version">)
 * and compares it with the stamp this page loaded with. When a newer version exists it reloads the page
 * by itself at a safe moment: when the shopper returns to the app, and never while they are typing,
 * have a menu / cart / dialog open, or are in the middle of the Find Your Attar quiz.
 * In the installed app it also shows its banner with an Update button (command --update; --dismiss hides it).
 * Attributes: `version` (stamp this page loaded with), `data-check-url` (Liquid routes.root_url).
 */
const RELOADED_KEY = 'alqalam-site-update-reloaded';
const DISMISSED_KEY = 'alqalam-site-update-dismissed';
const MIN_GAP_MS = 2 * 60 * 1000;
const INTERVAL_MS = 10 * 60 * 1000;
const STAMP_PATTERN = /<meta\s+name="site-version"\s+content="([^"]+)"/i;
const HASH_PATTERN = /^[a-f0-9]{32}$/i;

class SSiteUpdate extends HTMLElement {
  connectedCallback() {
    this.hidden = true; // stays hidden in the browser; the installed app shows the Update banner when needed
    this.currentVersion = (this.getAttribute('version') || '').trim();
    this.checkUrl = this.dataset.checkUrl || '/';
    if (!this.currentVersion) return;
    this.lastCheck = Date.now();
    this.pending = false;
    this.controller = new AbortController();
    const { signal } = this.controller;

    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.visibilityState !== 'visible') return;
        // Returning to the app: apply a waiting update first, otherwise look for one.
        if (this.pending) this.reloadIfSafe();
        else this.check(true);
      },
      { signal },
    );
    window.addEventListener('pageshow', (event) => { if (event.persisted) this.check(true); }, { signal });
    window.addEventListener('online', () => this.check(false), { signal });
    this.timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') this.check(false);
    }, INTERVAL_MS);

    // Installed app: show an "Update" button as soon as a newer version exists (instead of waiting silently).
    this.isApp =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: minimal-ui)').matches ||
      window.navigator.standalone === true;
    this.addEventListener('command', (event) => {
      if (event.command === '--update') this.applyUpdate();
      if (event.command === '--dismiss') {
        this.hidden = true;
        try { window.sessionStorage.setItem(DISMISSED_KEY, this.latestVersion || ''); } catch (error) { /* ignore */ }
      }
    }, { signal });
    if (this.isApp) {
      this.firstTimer = window.setTimeout(() => { this.lastCheck = 0; this.check(false); }, 4000);
    }
  }

  async fetchLatest() {
    const sep = this.checkUrl.includes('?') ? '&' : '?';
    try {
      const response = await fetch(`${this.checkUrl}${sep}view=site-version`, {
        cache: 'no-store',
        credentials: 'same-origin',
        signal: this.controller.signal,
      });
      if (response.ok) {
        const text = (await response.text()).trim();
        if (HASH_PATTERN.test(text)) return text;
        const match = text.match(STAMP_PATTERN);
        if (match) return match[1].trim();
      }
    } catch (error) {
      if (this.controller.signal.aborted) return '';
    }
    // Fallback: read the stamp from the full home page.
    try {
      const response = await fetch(this.checkUrl, { cache: 'no-store', credentials: 'same-origin', signal: this.controller.signal });
      if (!response.ok) return '';
      const match = (await response.text()).match(STAMP_PATTERN);
      return match ? match[1].trim() : '';
    } catch (error) {
      return '';
    }
  }

  async check(resumed) {
    if (this.checking || Date.now() - this.lastCheck < MIN_GAP_MS) return;
    this.checking = true;
    this.lastCheck = Date.now();
    try {
      const latest = await this.fetchLatest();
      if (!latest || latest.trim() === this.currentVersion) return;
      this.latestVersion = latest;
      this.pending = true;
      if (this.isApp) this.showBanner();
      // Reload right away only when the shopper has just come back; mid-browsing, wait for the next return.
      if (resumed) this.reloadIfSafe();
    } finally {
      this.checking = false;
    }
  }

  isBusy() {
    const active = document.activeElement;
    if (active && active.matches && active.matches('input, textarea, select, [contenteditable="true"]')) return true;
    try {
      if (document.querySelector('dialog[open], [popover]:popover-open')) return true;
    } catch (error) {
      if (document.querySelector('dialog[open]')) return true;
    }
    if (document.querySelector('.aq-finder__quiz:not([hidden])')) return true;
    return false;
  }

  showBanner() {
    try {
      if (window.sessionStorage.getItem(DISMISSED_KEY) === this.latestVersion) return;
    } catch (error) { /* ignore */ }
    if (this.querySelector('[data-update-cta]')) this.hidden = false;
  }

  applyUpdate() {
    try { window.sessionStorage.setItem(RELOADED_KEY, this.latestVersion || ''); } catch (error) { /* ignore */ }
    this.pending = false;
    window.location.reload();
  }

  reloadIfSafe() {
    if (!this.pending || document.visibilityState !== 'visible' || this.isBusy()) return;
    // Never loop: reload at most once for the same new version in this session.
    try {
      if (window.sessionStorage.getItem(RELOADED_KEY) === this.latestVersion) return;
      window.sessionStorage.setItem(RELOADED_KEY, this.latestVersion || '');
    } catch (error) {
      /* storage unavailable: still reload once for this page */
    }
    this.pending = false;
    window.location.reload();
  }

  disconnectedCallback() {
    if (this.controller) this.controller.abort();
    if (this.timer) window.clearInterval(this.timer);
    if (this.firstTimer) window.clearTimeout(this.firstTimer);
  }
}

if (!customElements.get('s-site-update')) customElements.define('s-site-update', SSiteUpdate);
